import { useMemo } from 'react'
import { type ChartBar } from './storeCubeCartonUpdateMockData'

interface Props {
  bars: ChartBar[]
}

const WIDTH = 760
const HEIGHT = 320
const PAD_LEFT = 48
const PAD_RIGHT = 16
const PAD_TOP = 16
const PAD_BOTTOM = 28

// Colors are inherent to this report's semantics (not the app's teal
// palette) — approximated from the reference screenshot.
const CORE_COLOR = '#0B4F9E'
const SEA_ROLLOVER_COLOR = '#F4A6A1'
const SEA_ON_TIME_COLOR = '#E6117A'
const GRID_COLOR = '#e7eaef'
const LABEL_COLOR = '#1c1c1c'
const AXIS_TICK_COLOR = '#6b7280'

const Y_MAX = 1000

export function StoreCubeCartonUpdateChart({ bars }: Props) {
  const { plotW, plotH, slot, barWidth } = useMemo(() => {
    const plotW = WIDTH - PAD_LEFT - PAD_RIGHT
    const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM
    const slot = plotW / bars.length
    const barWidth = slot * 0.62
    return { plotW, plotH, slot, barWidth }
  }, [bars.length])

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white p-4 shadow-sm">
      <div className="mb-2 text-sm font-semibold text-foreground">
        Average Metric Per Store
      </div>
      <div className="mb-3 flex items-center gap-4 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ background: SEA_ON_TIME_COLOR }}
          />
          SEA On Time
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ background: SEA_ROLLOVER_COLOR }}
          />
          SEA Rollover
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ background: CORE_COLOR }}
          />
          Core
        </span>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        preserveAspectRatio="none"
      >
        <g transform={`translate(${PAD_LEFT}, ${PAD_TOP})`}>
          {/* Gridlines + labels at 500 and 1,000, matching the screenshot */}
          {[500, 1000].map(tick => {
            const y = plotH - (tick / Y_MAX) * plotH
            return (
              <g key={tick}>
                <line
                  x1={0}
                  x2={plotW}
                  y1={y}
                  y2={y}
                  stroke={GRID_COLOR}
                  strokeWidth={1}
                />
                <text
                  x={-8}
                  y={y + 3}
                  textAnchor="end"
                  fontSize={10}
                  fill={AXIS_TICK_COLOR}
                >
                  {tick.toLocaleString('en-US')}
                </text>
              </g>
            )
          })}

          {/* Stacked bars: SEA On Time (bottom), SEA Rollover (thin middle), Core (top) */}
          {bars.map((b, i) => {
            const x = i * slot + (slot - barWidth) / 2
            const onTimeH = (b.seaOnTime / Y_MAX) * plotH
            const rolloverH = (b.seaRollover / Y_MAX) * plotH
            const coreH = (b.core / Y_MAX) * plotH

            const onTimeY = plotH - onTimeH
            const rolloverY = onTimeY - rolloverH
            const coreY = rolloverY - coreH

            return (
              <g key={b.period}>
                <rect
                  x={x}
                  y={onTimeY}
                  width={barWidth}
                  height={onTimeH}
                  fill={SEA_ON_TIME_COLOR}
                >
                  <title>{`SEA On Time — ${Math.round(b.seaOnTime).toLocaleString('en-US')}`}</title>
                </rect>
                {onTimeH > 12 && (
                  <text
                    x={x + barWidth / 2}
                    y={onTimeY + onTimeH / 2 + 4}
                    textAnchor="middle"
                    fontSize={11}
                    fill="#fff"
                  >
                    {Math.round(b.seaOnTime).toLocaleString('en-US')}
                  </text>
                )}

                <rect
                  x={x}
                  y={rolloverY}
                  width={barWidth}
                  height={rolloverH}
                  fill={SEA_ROLLOVER_COLOR}
                >
                  <title>{`SEA Rollover — ${Math.round(b.seaRollover).toLocaleString('en-US')}`}</title>
                </rect>
                {rolloverH > 12 && (
                  <text
                    x={x + barWidth / 2}
                    y={rolloverY + rolloverH / 2 + 4}
                    textAnchor="middle"
                    fontSize={10}
                    fill="#fff"
                  >
                    {Math.round(b.seaRollover).toLocaleString('en-US')}
                  </text>
                )}

                <rect
                  x={x}
                  y={coreY}
                  width={barWidth}
                  height={coreH}
                  fill={CORE_COLOR}
                >
                  <title>{`Core — ${Math.round(b.core).toLocaleString('en-US')}`}</title>
                </rect>
                {coreH > 12 && (
                  <text
                    x={x + barWidth / 2}
                    y={coreY + coreH / 2 + 4}
                    textAnchor="middle"
                    fontSize={11}
                    fill="#fff"
                  >
                    {Math.round(b.core).toLocaleString('en-US')}
                  </text>
                )}

                <text
                  x={x + barWidth / 2}
                  y={plotH + 16}
                  textAnchor="middle"
                  fontSize={10}
                  fill={LABEL_COLOR}
                >
                  {b.period}
                </text>
              </g>
            )
          })}

          {/* X-axis baseline */}
          <line
            x1={0}
            x2={plotW}
            y1={plotH}
            y2={plotH}
            stroke={GRID_COLOR}
            strokeWidth={1}
          />
        </g>
      </svg>
    </div>
  )
}
